import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Globe, Loader2, Save, CheckCircle, XCircle } from "lucide-react";

const API = "/api";
const SOURCE_LABEL = { settings: "Saved here", server_env: "Server default", none: "None set" };

export const ProxyListCard = ({ headers }) => {
  const [current, setCurrent] = useState({ source: "none", proxies: [] });
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);

  const load = async () => {
    try { setCurrent((await axios.get(`${API}/settings/proxy-list`, { headers })).data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/proxy-list`, { proxies: text }, { headers });
      toast.success(r.data.saved ? `${r.data.saved} proxies saved — bots rotate through them` : "Proxy list cleared");
      setText(""); setResults(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save proxies");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setResults(null);
    try { setResults((await axios.post(`${API}/settings/proxy-list/test`, {}, { headers })).data.results); }
    catch { toast.error("Proxy test failed"); }
    finally { setTesting(false); }
  };

  return (
    <div data-testid="proxy-list-card" className="card-base">
      <div className="card-header flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">
          <Globe className="w-3.5 h-3.5 text-[var(--accent-teal)]" />Order Proxies
        </span>
        <span data-testid="proxy-source" className="text-[10px] text-[var(--text-secondary)]">
          {SOURCE_LABEL[current.source]} · {current.proxies.length} active
        </span>
      </div>
      <div className="card-content space-y-3">
        <div data-testid="proxy-active-list" className="space-y-1">
          {current.proxies.map((p, i) => (
            <div key={i} className="text-[10px] font-mono text-[var(--text-secondary)] truncate">{i + 1}. {p}</div>
          ))}
        </div>
        <textarea data-testid="proxy-list-input" value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder={"Paste one per line, e.g.\n80.224.242.16:12323:user:pass\nsocks5://user:pass@host:port"}
          className="input-base w-full text-xs font-mono p-2 rounded-md resize-y" />
        <p className="text-[10px] text-[var(--text-secondary)]">Saving replaces the active list for all bots. Orders rotate through the proxies and fail over to the next one.</p>
        <div className="flex gap-2">
          <Button data-testid="proxy-save-btn" onClick={save} disabled={saving || !text.trim()} className="btn-primary flex-1 text-xs h-8">
            {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}Save Proxies
          </Button>
          <Button data-testid="proxy-test-btn" onClick={test} disabled={testing || !current.proxies.length} variant="outline" className="flex-1 text-xs h-8">
            {testing ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Globe className="w-3 h-3 mr-1.5" />}Test Proxies
          </Button>
        </div>
        {results && (
          <div data-testid="proxy-test-results" className="space-y-1">
            {results.map((r, i) => (
              <div key={i} data-testid={`proxy-test-result-${i}`} className="flex items-center gap-2 text-[10px] font-mono">
                {r.ok ? <CheckCircle className="w-3 h-3 text-[var(--accent-teal)] shrink-0" /> : <XCircle className="w-3 h-3 text-[var(--accent-red)] shrink-0" />}
                <span className="truncate text-[var(--text-secondary)]">{r.proxy}</span>
                <span className="ml-auto shrink-0" style={{ color: r.ok ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                  {r.ok ? `${r.country} · ${r.ms}ms` : (r.blocked ? `GEO-BLOCKED (${r.country})` : r.error || `HTTP ${r.clob_status}`)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
